import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Alert, AlertDescription } from "../ui/alert";
import { Mail, Loader2, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import http from "@/lib/http";

const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  clinicId: z.string().min(1, "Please select a clinic"),
});

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [clinics, setClinics] = useState([]);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
      clinicId: "",
    },
  });

  // Function to fetch clinics when email is valid
  const fetchClinics = async (email) => {
    if (!email || !email.includes("@")) return;

    setLoadingClinics(true);
    try {
      const response = await http.get(`/therapists/find-clinics/${email}`);
      setClinics(response.data || []);
    } catch (error) {
      console.error("Error fetching clinics:", error);
      setClinics([]);
    } finally {
      setLoadingClinics(false);
    }
  };

  // Watch email field for changes
  const watchedEmail = form.watch("email");

  // Fetch clinics when email changes and is valid
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (watchedEmail && watchedEmail.includes("@")) {
        fetchClinics(watchedEmail);
      } else {
        setClinics([]);
        form.setValue("clinicId", "");
      }
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timeoutId);
  }, [watchedEmail]);

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      await http.post("/auth/forgot-password", {
        email: data.email,
        clinicId: data.clinicId,
      });
      setSuccess(true);
      toast.success("Password reset email sent!");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to send reset email. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="space-y-1 pb-8">
            <div className="flex items-center justify-center mb-6">
              <img
                src="/logo-bat.png"
                alt="BookaTherapy"
                className="h-12 w-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-center text-slate-800">
              Reset Your Password
            </CardTitle>
            <CardDescription className="text-center text-slate-600">
              Enter your email address and we'll send you a link to reset your
              password
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {success ? (
              <div className="space-y-6">
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    We've sent a password reset link to your email address.
                    Please check your inbox and follow the instructions.
                  </AlertDescription>
                </Alert>
                <Button
                  onClick={() => navigate("/login")}
                  variant="outline"
                  className="w-full">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Login
                </Button>
              </div>
            ) : (
              <>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700">
                            Email Address
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                {...field}
                                type="email"
                                placeholder="therapist@clinic.com"
                                className="pl-10 h-11 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                                disabled={loading}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="clinicId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700">
                            Select Clinic
                          </FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={
                                loading ||
                                loadingClinics ||
                                clinics.length === 0
                              }>
                              <SelectTrigger className="text-start h-11 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500">
                                <SelectValue
                                  placeholder={
                                    loadingClinics
                                      ? "Loading clinics..."
                                      : clinics.length === 0
                                      ? "Enter a valid email to see clinics"
                                      : "Select your clinic"
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent className="max-w-sm">
                                {clinics.map((clinic) => (
                                  <SelectItem
                                    key={clinic._id || clinic.id}
                                    value={clinic._id || clinic.id}>
                                    <div className="flex flex-col max-w-xs">
                                      <span className="font-medium truncate">
                                        {clinic.clinicName}
                                      </span>
                                      {clinic.address && (
                                        <span className="text-sm text-slate-500 truncate">
                                          {clinic.address}
                                        </span>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full h-11 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                      disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send Reset Link"
                      )}
                    </Button>
                  </form>
                </Form>

                <div className="text-center">
                  <Button
                    onClick={() => navigate("/login")}
                    variant="ghost"
                    className="text-sm text-slate-600 hover:text-slate-800">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Login
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
